/**
 * @file CORS diagnostic page.
 * @description Interactive page for validating API CORS behavior, auth headers, and endpoint connectivity.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '../components/ui';
import { testSpecificEndpoints } from '../utils/corsTest';

const CorsTest: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const API_BASE_URL = useMemo(
    () => import.meta.env['VITE_API_BASE_URL'] || 'https://apijiggle.safebima.com/api',
    [],
  );
  const DASHBOARD_URL = `${API_BASE_URL}/dashboard`;

  const addResult = useCallback((message: string) => {
    setTestResults(prev => {
      const next = [...prev, `${new Date().toLocaleTimeString()}: ${message}`];
      return next.slice(-200);
    });
  }, []);

  const formatError = (error: unknown) => (error instanceof Error ? error.message : 'Unknown error');

  const runTest = useCallback(async (label: string, testFn: () => Promise<void>) => {
    setIsLoading(true);
    try {
      addResult(`${label}...`);
      await testFn();
    } catch (error) {
      addResult(`❌ ${label} failed: ${formatError(error)}`);
    } finally {
      setIsLoading(false);
    }
  }, [addResult]);

  const requestAndLog = useCallback(
    async (label: string, requestInit: RequestInit, showHeaders: boolean = false) => {
      const response = await fetch(DASHBOARD_URL, requestInit);
      addResult(`✅ ${label}: ${response.status} ${response.statusText}`);
      addResult(`Response type: ${response.type}`);
      if (showHeaders) {
        const headers: string[] = [];
        response.headers.forEach((value, key) => {
          headers.push(`${key}: ${value}`);
        });
        addResult(`Headers: ${headers.join(', ')}`);
      }
    },
    [DASHBOARD_URL, addResult],
  );

  const testBasicFetch = async () => {
    await runTest('Starting basic fetch test', async () => {
      await requestAndLog(
        'Basic fetch successful',
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json',
          },
        },
        true,
      );
    });
  };

  const testWithCredentials = async () => {
    await runTest('Testing with credentials', async () => {
      await requestAndLog('Credentials test successful', {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });
    });
  };

  const testOptionsPreflight = async () => {
    await runTest('Testing OPTIONS preflight', async () => {
      await requestAndLog('OPTIONS test successful', {
        method: 'OPTIONS',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
    });
  };

  const testWithAuth = async () => {
    const token = localStorage.getItem('jiggle_token');
    if (!token) {
      addResult('❌ No token found in localStorage');
      return;
    }

    await runTest('Testing with Authorization header', async () => {
      await requestAndLog('Auth test successful', {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    });
  };

  const testFailingEndpoints = async () => {
    await runTest('Testing specific failing endpoints', async () => {
      await testSpecificEndpoints();
      addResult('✅ Failing endpoints test completed - check console for details');
    });
  };


  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">CORS Test Page</h1>
        <p className="text-red-100">Debug CORS issues with your API</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Test Controls</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            onClick={testBasicFetch} 
            disabled={isLoading}
            className="bg-blue-500 hover:bg-blue-600"
          >
            Basic Fetch
          </Button>
          <Button 
            onClick={testWithCredentials} 
            disabled={isLoading}
            className="bg-green-500 hover:bg-green-600"
          >
            With Credentials
          </Button>
          <Button 
            onClick={testOptionsPreflight} 
            disabled={isLoading}
            className="bg-purple-500 hover:bg-purple-600"
          >
            OPTIONS Test
          </Button>
          <Button 
            onClick={testWithAuth} 
            disabled={isLoading}
            className="bg-orange-500 hover:bg-orange-600"
          >
            With Auth
          </Button>
        </div>
        
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Additional Tests</h3>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
            <Button 
              onClick={testFailingEndpoints} 
              disabled={isLoading}
              className="bg-pink-500 hover:bg-pink-600"
            >
              Test Failing Endpoints
            </Button>
          </div>
        </div>
        <div className="mt-4">
          <Button 
            onClick={clearResults} 
            variant="outline"
            className="bg-gray-100 hover:bg-gray-200"
          >
            Clear Results
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Test Results</h2>
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
          {testResults.length === 0 ? (
            <p className="text-gray-500">No test results yet. Click a test button above.</p>
          ) : (
            <div className="space-y-2">
              {testResults.map((result, index) => (
                <div key={index} className="text-sm font-mono">
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Debugging Information</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold">Current Origin:</h3>
            <p className="text-sm text-gray-600">{window.location.origin}</p>
          </div>
          <div>
            <h3 className="font-semibold">API Base URL:</h3>
            <p className="text-sm text-gray-600">{API_BASE_URL}</p>
          </div>
          <div>
            <h3 className="font-semibold">Token Status:</h3>
            <p className="text-sm text-gray-600">
              {localStorage.getItem('jiggle_token') ? '✅ Token present' : '❌ No token'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorsTest;
